import React from 'react';
import { C } from '../theme';

// Render inline **bold** + *italic* into gold-highlighted spans
function renderInline(text, keyBase) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyBase}-b${i}`} style={{ color: C.gold, fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={`${keyBase}-i${i}`} style={{ color: C.gold2, fontStyle: 'italic' }}>{part.slice(1, -1)}</em>;
    }
    return <span key={`${keyBase}-t${i}`}>{part}</span>;
  });
}

export default function AIResponse({ text }) {
  if (!text) return null;

  const lines = text.split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();

    // Empty line → small spacer
    if (line === '') { i++; continue; }

    // Divider ---
    if (/^-{3,}$/.test(line)) {
      blocks.push({ type: 'divider' });
      i++;
      continue;
    }

    // Quote block (one or more consecutive > lines)
    if (line.startsWith('>')) {
      const quoteLines = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ''));
        i++;
      }
      // Last line starting with — or - is the source attribution
      let source = null;
      if (quoteLines.length > 1 && /^[—-]/.test(quoteLines[quoteLines.length - 1])) {
        source = quoteLines.pop().replace(/^[—-]\s?/, '');
      }
      blocks.push({ type: 'quote', text: quoteLines.join(' '), source });
      continue;
    }

    // Section header (starts with ✦)
    if (line.startsWith('✦')) {
      blocks.push({ type: 'header', text: line.replace(/^✦\s?/, '') });
      i++;
      continue;
    }

    // Numbered/bullet step line
    if (/^([٠-٩0-9]+[.\-)]|[-•])\s/.test(line)) {
      blocks.push({ type: 'step', text: line.replace(/^([٠-٩0-9]+[.\-)]|[-•])\s/, '') , marker: line.match(/^([٠-٩0-9]+[.\-)]|[-•])/)[0] });
      i++;
      continue;
    }

    // Normal paragraph
    blocks.push({ type: 'para', text: line });
    i++;
  }

  return (
    <div style={{ fontFamily: 'Cairo, sans-serif' }}>
      {blocks.map((b, idx) => {
        if (b.type === 'divider') {
          return <hr key={idx} style={{ border: 'none', borderTop: `1px solid ${C.border}`, margin: '14px 0' }} />;
        }
        if (b.type === 'header') {
          return (
            <div key={idx} style={{ fontFamily: 'Amiri, serif', fontSize: 16, color: C.gold, fontWeight: 700, margin: '16px 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>✦</span>{renderInline(b.text, `h${idx}`)}
            </div>
          );
        }
        if (b.type === 'quote') {
          return (
            <div key={idx} style={{ background: 'rgba(212,168,71,.05)', border: `1px solid rgba(212,168,71,.14)`, borderRight: `3px solid ${C.goldDim}`, borderRadius: 12, padding: '12px 16px', margin: '10px 0' }}>
              <p style={{ fontFamily: 'Amiri, serif', fontSize: 14.5, color: C.gold2, lineHeight: 1.9, fontStyle: 'italic', margin: 0, textAlign: 'right' }}>
                {renderInline(b.text, `q${idx}`)}
              </p>
              {b.source && <span style={{ fontSize: 11, color: C.text2, display: 'block', marginTop: 6 }}>— {b.source}</span>}
            </div>
          );
        }
        if (b.type === 'step') {
          return (
            <div key={idx} style={{ display: 'flex', gap: 8, margin: '4px 0', fontSize: 14, lineHeight: 1.8, color: C.text, textAlign: 'right' }}>
              <span style={{ color: C.gold, flexShrink: 0, fontWeight: 700 }}>{b.marker}</span>
              <span>{renderInline(b.text, `s${idx}`)}</span>
            </div>
          );
        }
        // paragraph
        return (
          <p key={idx} style={{ fontSize: 14, lineHeight: 1.9, color: C.text, margin: '8px 0', textAlign: 'right' }}>
            {renderInline(b.text, `p${idx}`)}
          </p>
        );
      })}
    </div>
  );
}
